// Scheduled Reports: form state to and from the API body, client checks, the summary texts and who may
// open what. Pure, so Jest covers it; the service enforces the real rules.

// ISO order: index 0 is day 1, Monday.
export const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const GRANTED = ["ALL", "READ"];

// "ALL", "READ" or null, from the Solution Features > Scheduled Reports master.
export function reportsLevel(metaData) {
  if (metaData && metaData.unscopedAdmin) return "ALL";
  const level = metaData?.modulePermissions?.["Solution Features"]?.["Scheduled Reports"];
  return GRANTED.includes(level) ? level : null;
}

export function scheduleText(schedule) {
  if (!schedule) return "";
  const { interval, dayOfWeek, dayOfMonth, sendAt, timeZone } = schedule;
  let when = interval;
  if (interval === "daily") when = "Daily";
  if (interval === "weekly") when = `Every ${DAYS[dayOfWeek - 1] || "week"}`;
  if (interval === "monthly") when = `Monthly on day ${dayOfMonth}`;
  return `${when} at ${sendAt} (${timeZone})`;
}

function format(value, options, timeZone) {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) return "";
  try {
    return new Intl.DateTimeFormat("en-GB", { ...options, timeZone }).format(date);
  } catch (e) {
    return new Intl.DateTimeFormat("en-GB", options).format(date);
  }
}

const DAY_FORMAT = { day: "numeric", month: "short", year: "numeric" };
const TIME_FORMAT = { weekday: "short", ...DAY_FORMAT, hour: "2-digit", minute: "2-digit", hourCycle: "h23" };

export const formatWhen = (value, timeZone) => format(value, TIME_FORMAT, timeZone);
export const formatDay = (value, timeZone) => format(value, DAY_FORMAT, timeZone);

// A run's window in its own zone: it ends at the send time.
export function periodText(run) {
  if (!run || !run.periodStart || !run.periodEnd) return "";
  return `${formatWhen(run.periodStart, run.timeZone)} to ${formatWhen(run.periodEnd, run.timeZone)}`;
}

export function scopeText(scope, services) {
  if (!scope || scope.mode === "all") return "All solutions";
  const ids = (scope.serviceIds || []).map(String);
  const names = ids.map((id) => (services || []).find((s) => String(s.id) === id)?.name).filter(Boolean);
  const shown = names.slice(0, 2).join(", ");
  const more = ids.length - Math.min(names.length, 2);
  return more > 0 ? `${shown}${shown ? " and " : ""}${more} more` : shown;
}

const CHANGE_WORDS = {
  created: "Created",
  edited: "Edited",
  paused: "Paused",
  resumed: "Resumed",
  deleted: "Deleted",
  addressAdded: "Address added",
  addressRemoved: "Address removed",
};

export function changeText({ kind, fields, address } = {}) {
  const word = CHANGE_WORDS[kind] || kind;
  if (address) return `${word}: ${address}`;
  return fields && fields.length ? `${word}: ${fields.join(", ")}` : word;
}

// ---- Presets from the configuration (the tenant's report profile): { key, label, default?, interval?, sections }.
export const presetKey = (preset) => preset.key;
export const presetLabel = (preset) => preset.label || preset.key;

export function applyPreset(form, preset) {
  const next = { ...form, preset: preset.key };
  if (Array.isArray(preset.sections)) next.sections = preset.sections;
  if (preset.interval) next.schedule = { ...form.schedule, interval: preset.interval };
  return next;
}

export function newForm(config, timeZone) {
  const form = {
    name: "",
    preset: "",
    sections: [],
    scope: { mode: "solutions", serviceIds: [] },
    schedule: { interval: "weekly", dayOfWeek: 1, dayOfMonth: 1, sendAt: "07:00", timeZone },
    objectivePreset: "",
    recipients: [],
  };
  const presets = (config && config.presets) || [];
  const first = presets.find((p) => p.default) || presets[0];
  return first ? applyPreset(form, first) : form;
}

// A section's stored object is kept as it came, unknown option keys included, so an untouched report
// round-trips unchanged and the service sees no edit.
export function formFromReport(report) {
  const schedule = report.schedule || {};
  const scope = report.scope || {};
  return {
    name: report.name || "",
    preset: report.preset || "",
    sections: report.sections || [],
    scope: { mode: scope.mode || "solutions", serviceIds: (scope.serviceIds || []).map(String) },
    schedule: {
      interval: schedule.interval || "weekly",
      dayOfWeek: schedule.dayOfWeek || 1,
      dayOfMonth: schedule.dayOfMonth || 1,
      sendAt: schedule.sendAt || "07:00",
      timeZone: schedule.timeZone || "UTC",
    },
    objectivePreset: report.ai?.objective?.preset || "",
    recipients: report.recipients || [],
    version: report.version,
  };
}

// The body of POST /reports/, or with `stored` of PUT /reports/:id.
export function buildBody(form, stored) {
  const { interval, dayOfWeek, dayOfMonth, sendAt, timeZone } = form.schedule;
  const schedule = { interval, sendAt, timeZone };
  if (interval === "weekly") schedule.dayOfWeek = Number(dayOfWeek);
  if (interval === "monthly") schedule.dayOfMonth = Number(dayOfMonth);
  const body = {
    name: form.name.trim(),
    sections: form.sections,
    scope:
      form.scope.mode === "all"
        ? { mode: "all", serviceIds: [] }
        : { mode: "solutions", serviceIds: form.scope.serviceIds },
    schedule,
    recipients: form.recipients,
  };
  if (form.preset || (stored && stored.preset)) body.preset = form.preset;
  const storedObjective = stored?.ai?.objective?.preset || "";
  if (form.objectivePreset !== storedObjective) {
    body.ai = { enabled: !!stored?.ai?.enabled, objective: { preset: form.objectivePreset } };
  }
  if (stored) body.version = form.version;
  return body;
}

// The body of preview and test send: the draft as it stands, with what the form does not edit, so a saved
// report previews as it will really go out.
export function buildDraft(form, stored) {
  const body = buildBody(form);
  if (!stored) return body;
  return { ...body, reportId: stored.id, tiles: stored.tiles || [], options: stored.options || {} };
}

// A test send answers 200 even when its copy did not go: the mail account's rules or the staging gate
// can hold it, and the answer says why.
export function testSendMessage(answer, email) {
  const address = (answer && answer.address) || email || "your address";
  if (answer && answer.state === "sent") return { severity: "success", text: `The test report was sent to ${address}.` };
  const why = (answer && (answer.reason || answer.label)) || "no reason was given";
  return { severity: "warning", text: `The test report was not sent to ${address}: ${why}.` };
}

// ---- Client checks, on the same paths as the service's errors[].
function optionProblem(opt, value) {
  if (value === undefined) return "";
  if (opt.type === "number") {
    if (typeof value !== "number" || !Number.isFinite(value)) return "Enter a number";
    if (opt.min !== undefined && value < opt.min) return `Must be at least ${opt.min}`;
    if (opt.max !== undefined && value > opt.max) return `Must be at most ${opt.max}`;
  }
  if (opt.type === "multiselect" && Array.isArray(value) && value.length < (opt.minCount || 0)) {
    return `Choose at least ${opt.minCount}`;
  }
  return "";
}

export function validateForm(form, config) {
  const errors = {};
  const limits = (config && config.limits) || {};
  const catalogue = (config && config.sections) || [];
  const name = form.name.trim();
  if (!name) errors.name = "Enter a name";
  else if (limits.maxNameLength && name.length > limits.maxNameLength) {
    errors.name = `At most ${limits.maxNameLength} characters`;
  }
  if (form.scope.mode !== "all" && !form.scope.serviceIds.length) errors.scope = "Choose at least one solution";
  if (!form.sections.length) errors.sections = "Choose at least one section";
  form.sections.forEach((section, i) => {
    const schema = catalogue.find((c) => c.key === section.key)?.optionsSchema || {};
    Object.entries(schema).forEach(([option, opt]) => {
      const problem = optionProblem(opt, (section.options || {})[option]);
      if (problem) errors[`sections.${i}.options.${option}`] = problem;
    });
  });
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(form.schedule.sendAt || "")) errors["schedule.sendAt"] = "Choose a send time";
  if (!form.schedule.timeZone) errors["schedule.timeZone"] = "Choose a time zone";
  if (limits.maxRecipients && form.recipients.length > limits.maxRecipients) {
    errors.recipients = `At most ${limits.maxRecipients} addresses`;
  }
  return errors;
}

// ---- Recipients: typed or pasted, one per line or comma-separated, trimmed, lower-cased, kept once.
const ADDRESS = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function addAddresses(recipients, text) {
  const typed = String(text || "")
    .split(/[\s,;]+/)
    .map((a) => a.trim().toLowerCase())
    .filter(Boolean);
  const invalid = typed.filter((a) => !ADDRESS.test(a));
  const valid = typed.filter((a) => ADDRESS.test(a));
  return { recipients: [...new Set([...recipients, ...valid])], invalid };
}

export function addressWarnings(result) {
  const lines = [];
  if (!result) return lines;
  if (result.outsideAccess) lines.push("This person will see sites outside their own access.");
  if (result.unsubscribedAt) lines.push(`This address unsubscribed on ${formatDay(result.unsubscribedAt)}.`);
  return lines;
}

// ---- The scope picker: the features the chosen sections need, and the solutions the user holds in full
// for them (no group limit, eligible for Scheduled Reports, the features there).
export function sectionFeatures(sections, catalogue) {
  const features = (sections || []).flatMap((s) => (catalogue || []).find((c) => c.key === s.key)?.requires?.features || []);
  return [...new Set(features)];
}

export function solutionsOffered(metaData, features) {
  const services = (metaData && metaData.services) || [];
  if (metaData && metaData.unscopedAdmin) return services;
  const masters = metaData?.modulePermissions?.["Solution Features"] || {};
  return services.filter((s) => {
    const groupId = s.group && s.group.id;
    if (groupId !== undefined && groupId !== null && groupId !== "") return false;
    if (metaData?.srEligible?.[String(s.id)] === false) return false;
    return (features || []).every((f) => GRANTED.includes(masters[f]) && (s.tabs || []).some((t) => t && t.name === f));
  });
}

const WINDOW_NOTES = {
  daily: "Each report covers the 24 hours up to its send time, compared with the 24 hours before.",
  weekly: "Each report covers the 7 days up to its send time, compared with the 7 days before.",
  monthly: "Each report covers the month up to its send time, compared with the month before. Months differ in length, so the two can differ by a few days.",
};

// The notes beside the schedule fields; the service computes every date itself.
export function scheduleNotes(schedule) {
  const note = WINDOW_NOTES[schedule.interval];
  return note ? [note] : [];
}
